import { ICorrespondence } from "../interfaces/Correspondence";
import { IParamResponseTime } from "../redux/types/types";
import moment from "moment";
import {
    calculateResponseDate, createOfficeMap,
    createParamsProcedure, formatCommunicationType, formatSettledResponse,
    getAttachmentStatus,
    getChannelName,
    getEmailStatus,
    getOfficeName,
    getResponseStatus,
    getTrafficLightColor,
    inputTypeMap, isExcludedInputType,
    toCamelCase,
} from "./correspondenceUtils";
import {IntlShape} from "react-intl";
import {generateExcelFile, generateExcelFileName} from "./downloadExcel";
import { CorrespondenceStatus } from "../enums/enums";

/**
 * Transforma datos de correspondencia para exportarlos a Excel.
 *
 * @param data - Lista de correspondencias.
 * @param paramResponseTime - Parámetros de tiempo de respuesta.
 * @param intl - Objeto de internacionalización para traducir textos.
 * @param productionOffices - Lista de oficinas de producción.
 * @returns Una promesa con los datos transformados para exportación.
 */
export const mapCorrespondenceToExcel = async (
    data: ICorrespondence[],
    paramResponseTime: IParamResponseTime,
    intl: IntlShape,
    productionOffices: any[]
): Promise<any[]> => {
    const paramsProcedure = createParamsProcedure(paramResponseTime);
    const emailStatusMap: Record<string, string> = {};

    await Promise.all(
        data.map(async (current) => {
            if (current.status != CorrespondenceStatus.INACTIVE) {
                emailStatusMap[current.id] = await getEmailStatus(current);
            }
        })
    );

    return await Promise.all(
        data.map(async (current) => {
            const dateObj = current.createAt?.seconds
                ? new Date(current.createAt.seconds * 1000)
                : null;

            const officeMap = createOfficeMap(productionOffices);

            let { responseDateParam, availableDays } = calculateResponseDate(current, paramResponseTime);


            if (current.inputType !== "EP") {
                availableDays = responseDateParam
                    ? responseDateParam.diff(moment(), "days")
                    : 0;
                availableDays = availableDays > 0 ? availableDays : 0;
            }

            const trafficLightColor = getTrafficLightColor(
                current,
                paramResponseTime,
                paramsProcedure
            );
            const attachmentStatus = getAttachmentStatus(current);
            const responseStatus = getResponseStatus(current, intl);
            const dependencyName = getOfficeName(
                current.settledAnswerId && current.producerDependency
                    ? current.producerDependency
                    : current.receivingDependency,
                officeMap
            );
            const inputTypeDescription = inputTypeMap[current.inputType] || current.inputType || "No Especificado";
            const emailStatus = emailStatusMap[current.id] || "No Aplica";
            const communicationTypeTranslated =
                intl.messages[current.communicationType?.option || ""] ||
                current.communicationType?.option;



            return {
                "NÚMERO DE RADICADO": current.id,
                "TIPO DE ENTRADA": inputTypeDescription,
                "TIPO DE CANAL": getChannelName(current.id, intl),
                "TIPO DE COMUNICACIÓN": communicationTypeTranslated,
                "ASUNTO": current.subject,
                "FECHA": dateObj ? moment(dateObj).format("YYYY-MM-DD") : "",
                "HORA": dateObj ? moment(dateObj).format("HH:mm:ss") : "",
                "DEPENDENCIA": toCamelCase(dependencyName),
                "DESTINATARIO": toCamelCase(current.addressee?.name),
                "REMITENTE": toCamelCase(current.externalSender?.name),
                "SEMÁFORO": trafficLightColor,
                "TIENE ADJUNTOS": attachmentStatus,
                "CORREO ENVIADO": emailStatus,
                "FECHA LÍMITE DE RESPUESTA": isExcludedInputType(current.inputType) ? "No aplica" : responseDateParam?.format("YYYY-MM-DD") || "",
                "DÍAS RESTANTES": isExcludedInputType(current.inputType) ? "No aplica" : availableDays,
                "ESTADO DE RESPUESTA": responseStatus,
            };
        })
    );
};

/**
 * Exporta la lista de correspondencias recibidas a un archivo de Excel.
 *
 * Esta función genera un archivo Excel con los datos de correspondencias recibidas, incluyendo información
 * sobre el número de radicado, tipo de canal, fecha, remitente, destinatario, dependencia y otros detalles relevantes.
 *
 * @param data - Lista de correspondencias recibidas a exportar.
 * @param intl - Objeto de internacionalización para traducción de textos.
 * @param productionOffices - Lista de oficinas de producción.
 * @returns Una promesa que genera y descarga el archivo Excel.
 */
export const mapCorrespondenceReceivedToExcel = async (data: any[], intl: any, productionOffices: any[]): Promise<void> => {

    if (!data || data.length === 0) {
        const emptyContent = [
            {
                "NÚMERO DE RADICADO": "No hay registros de correspondencia recibida",
                "T.C": "-",
                "ASUNTO": "-",
                "REMITENTE": "-",
                "DESTINATARIO": "-",
                "FECHA": "-",
                "RADICÓ": "-",
                "DEPENDENCIA": "-",
                "FIRMA": "-",
            },
        ];
        generateExcelFile("Correspondencia_recibida", emptyContent, () => generateExcelFileName("Correspondencia_recibida"))
        return;
    }

    const info = await Promise.all(
        data.map(async (current) => {
            const officeMap = createOfficeMap(productionOffices);
            const dependencyName = getOfficeName(
                current.settledAnswerId && current.producerDependency
                    ? current.producerDependency
                    : current.receivingDependency,
                officeMap
            );

            return {
                "# RADICADO": current.id,
                "CONSECUTIVO": current.id.slice(-9),
                "TIPO DE CANAL": getChannelName(current.id, intl),
                "FECHA": moment(current.createAt.toDate()).format("DD/MM/YYYY"),
                "TIPO COMUNICACIÓN": formatCommunicationType(current.communicationType, intl),
                "ASUNTO": current.subject,
                "REMITENTE": current.externalSender.name,
                "DESTINATARIO": current.addressee?.name,
                "DEPENDENCIA": dependencyName,
                "RADICÓ": current.owner.name,
                "NOMBRE DE QUIEN RECIBE": "", // TODO: Añadir firma cuando esté definida la información.
            };
        })
    );

    generateExcelFile("Correspondencia_recibida", info, () => generateExcelFileName("Correspondencia_recibida"));

};

/**
 * Exporta el seguimiento de correspondencias recibidas a un archivo de Excel.
 *
 * Esta función genera un archivo Excel con el seguimiento de correspondencias recibidas, incluyendo información
 * sobre fechas de radicación y respuesta, días restantes para responder, estado de respuesta y semáforo de cumplimiento.
 *
 * @param data - Lista de correspondencias a exportar.
 * @param intl - Objeto de internacionalización para traducción de textos.
 * @param productionOffices - Lista de oficinas de producción.
 * @param paramResponseTime - Parámetros de tiempo de respuesta utilizados para calcular fechas y tiempos límite.
 * @returns Una promesa que genera y descarga el archivo Excel.
 */
export const mapTrackingOfReceivedCorrespondenceToExcel = async (data: any[], intl: any, productionOffices: any[], paramResponseTime: IParamResponseTime): Promise<void> => {

    if (!data || data.length === 0) {
        const emptyContent = [
            {
                "# RADICADO": "No hay registros de correspondencia recibida",
                "T.C": "-",
                "ASUNTO": "-",
                "REMITENTE": "-",
                "DESTINATARIO": "-",
                "FECHA RADICACIÓN": "-",
                "FECHA RESPUESTA": "-",
                "ESTADO RESPUESTA": "-",
                "RADICADO RESPUESTA": "-",
            },
        ];
        generateExcelFile("seguimiento_correspondencia_recibida", emptyContent, () => generateExcelFileName("seguimiento_correspondencia_recibida"))
        return;
    }


    const info = await Promise.all(
        data.map(async (current) => {

            const paramsProcedure = createParamsProcedure(paramResponseTime);
            const { responseDateParam, availableDays } = calculateResponseDate(current, paramResponseTime);
            const responseStatus = getResponseStatus(current, intl);
            const trafficLightColor = getTrafficLightColor(
                current,
                paramResponseTime,
                paramsProcedure
            );
            const answerData = current.dataAnswer;
            const attachmentStatus = answerData ? getAttachmentStatus(answerData) : "-";

            let emailStatus = "-";
            if(answerData){
                emailStatus = await getEmailStatus(answerData);
            }           

            return {
                "# RADICADO": current.id,
                "TIPO DE COMUNICACIÓN": formatCommunicationType(current.communicationType, intl),
                "TIPO DE CANAL": getChannelName(current.id, intl),
                "ASUNTO": current.subject,
                "REMITENTE": current.externalSender.name,
                "DESTINATARIO": current.addressee?.name,
                "FECHA RADICACIÓN": moment(current.createAt.toDate()).format("DD/MM/YYYY"),
                "FECHA RESPUESTA": isExcludedInputType(current.inputType) ? "No aplica" : responseDateParam?.format("YYYY-MM-DD") || "",
                "DÍAS RESTANTES": isExcludedInputType(current.inputType) ? "No aplica " : availableDays,
                "ESTADO DE RESPUESTA": responseStatus,
                "SEMÁFORO": trafficLightColor,
                "RADICADO RESPUESTA": formatSettledResponse(current.dataAnswer),
                "TIENE ADJUNTOS": attachmentStatus,
                "CORREO ENVIADO": emailStatus
            };
        })
    );
    generateExcelFile("seguimiento_corresp_recibida", info, () => generateExcelFileName("seguimiento_corresp_recibida"));

}
